import { useEffect, useMemo, useState } from "react";
import {
  AssignTaskRequest,
  ClioService,
  CustomFields,
  MatterPreviewResponse,
  TaskPreviewResponse,
  UsersModel,
} from "../../api";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { debounce } from "@mui/material/utils";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import FilterableSelect from "../../components/basic/FilterableSelect";

const MySwal = withReactContent(Swal);

const STAFF_TYPES = [
  CustomFields.ASSIGNED_PARALEGAL,
  CustomFields.RESPONSIBLE_ATTORNEY,
  CustomFields.SUPPORTING_ATTY,
  CustomFields.SENIOR_MANAGING_ATTY,
  CustomFields.LEGAL_ASSISTANT,
];

type Props = {
  open: boolean;
  onClose: () => void;
  onAssigned: () => void;
};

export default function AssignTaskDialog({ open, onClose, onAssigned }: Props) {
  const [matterOptions, setMatterOptions] = useState<readonly MatterPreviewResponse[]>([]);
  const [matterValue, setMatterValue] = useState<MatterPreviewResponse | null>(null);
  const [matterInputValue, setMatterInputValue] = useState("");
  const [taskInputValue, setTaskInputValue] = useState("");
  const [taskValue, setTaskValue] = useState<TaskPreviewResponse | null>(null);
  const [taskOptions, setTaskOptions] = useState<readonly TaskPreviewResponse[]>([]);
  const [usersList, setUsersList] = useState<UsersModel[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedUserType, setSelectedUserType] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setMatterValue(null);
    setMatterInputValue("");
    setTaskValue(null);
    setTaskInputValue("");
    setMatterOptions([]);
    setTaskOptions([]);
    setSelectedUser(null);
    setSelectedUserType(null);
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    ClioService.getApiClioClioUsers(true).then(setUsersList);
  }, [open]);

  const fetchMatters = useMemo(
    () =>
      debounce((input: string) => {
        ClioService.getApiClioMatters(input).then((response) => {
          const newOptions: MatterPreviewResponse[] = matterValue ? [matterValue] : [];
          if (response) {
            newOptions.push(...response);
          }
          setMatterOptions(newOptions);
        });
      }, 200),
    [matterValue]
  );

  useEffect(() => {
    if (!open || matterInputValue === "") {
      setMatterOptions(matterValue ? [matterValue] : []);
      return;
    }
    fetchMatters(matterInputValue);
  }, [open, matterValue, matterInputValue, fetchMatters]);

  const fetchTasks = useMemo(
    () =>
      debounce((input: string) => {
        ClioService.getApiClioTaskTypes(input).then((response) => {
          const newOptions: TaskPreviewResponse[] = taskValue ? [taskValue] : [];
          if (response) {
            newOptions.push(...response);
          }
          setTaskOptions(newOptions);
        });
      }, 200),
    [taskValue]
  );

  useEffect(() => {
    if (!open || taskInputValue === "") {
      setTaskOptions(taskValue ? [taskValue] : []);
      return;
    }
    fetchTasks(taskInputValue);
  }, [open, taskValue, taskInputValue, fetchTasks]);

  const handleClose = () => {
    if (submitting) {
      return;
    }
    resetForm();
    onClose();
  };

  const canSubmit =
    matterValue?.id != null &&
    taskValue?.id != null &&
    (selectedUser != null || selectedUserType != null);

  const assignTask = () => {
    if (!canSubmit) {
      MySwal.fire({
        icon: "warning",
        title: <p>Please complete all required fields.</p>,
      });
      return;
    }

    const request = {
      matterId: +matterValue!.id!,
      taskTypeId: +taskValue!.id!,
      assigneeId: selectedUser != null ? +selectedUser : null,
      assigneeType: selectedUserType,
    } as AssignTaskRequest;

    setSubmitting(true);
    ClioService.postApiClioAssignTask(request)
      .then(() => {
        MySwal.fire({
          icon: "success",
          title: <p>Task assigned successfully.</p>,
        });
        resetForm();
        onAssigned();
      })
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error assigning task.</p>,
        })
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Clio Task</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Autocomplete
            getOptionLabel={(option) =>
              (typeof option === "string" ? option : option.description) as string
            }
            filterOptions={(x) => x}
            options={matterOptions}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={matterValue}
            noOptionsText="Start typing matter"
            onChange={(_, selectedValue) => {
              setMatterValue(selectedValue);
            }}
            onInputChange={(_, newInputValue) => setMatterInputValue(newInputValue)}
            renderInput={(params) => (
              <TextField {...params} label="Matter" required fullWidth />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography variant="body2">{option.description}</Typography>
              </li>
            )}
          />

          <Autocomplete
            getOptionLabel={(option) =>
              (typeof option === "string" ? option : option.name) as string
            }
            filterOptions={(x) => x}
            options={taskOptions}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={taskValue}
            noOptionsText="Start typing task"
            onChange={(_, selectedValue) => setTaskValue(selectedValue)}
            onInputChange={(_, newInputValue) => setTaskInputValue(newInputValue)}
            renderInput={(params) => (
              <TextField {...params} label="Task" required fullWidth />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography variant="body2">{option.name}</Typography>
              </li>
            )}
          />

          <FilterableSelect
            label="Assignee"
            fullWidth
            required
            includeAll={false}
            value={selectedUser ?? undefined}
            options={usersList.map((user) => ({
              value: user.id!,
              label: user.name ?? "",
            }))}
            onChange={(userId) => setSelectedUser(String(userId))}
          />

          <FormControl fullWidth required>
            <InputLabel id="staff-type-label">Staff type</InputLabel>
            <Select
              labelId="staff-type-label"
              label="Staff type"
              value={selectedUserType ?? ""}
              onChange={(e) => setSelectedUserType(e.target.value || null)}
            >
              {STAFF_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="outlined" disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={assignTask}
          variant="contained"
          disabled={!canSubmit || submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          Assign Task
        </Button>
      </DialogActions>
    </Dialog>
  );
}
